// ─────────────────────────────────────────────────────────────────────────────
// Zero-width marker for btgpt "AI Conversation" replies.
//
// Shared (via the bt-common mirror) between the extension client
// (whatsapp-scheduler) and the backend (blueticks-api). btgpt replies sent into
// a linked WhatsApp group are `fromMe` — exactly like the user's own input in
// that group — so text is the only channel that can distinguish them. Every
// bridge reply gets this marker appended; both the extension (pre-dispatch) and
// the backend (dispatch seam) drop marked messages, and history rendering
// strips markers before the model sees them.
//
// Encoding: identical scheme to the chatbot's hidden bot identifier
// (gateway/chatbot/nodes/utils.ts) — each identifier char becomes 8 bits,
// 0 = U+200B (zero-width space), 1 = U+200C (zero-width non-joiner) — but with
// a DISTINCT identifier string so btgpt bridge replies and Quick Agent bot
// replies remain distinguishable.
// ─────────────────────────────────────────────────────────────────────────────

const AI_CONVERSATION_IDENTIFIER = 'BTGPT';

const ZERO_WIDTH_TAIL_RE = /[\u200B\u200C]+$/;
// Strip the marker AND the newline separator we add before it (see MARKER_SEP).
const MARKER_TAIL_STRIP_RE = /\n?[\u200B\u200C]+$/;

function encodeIdentifier(identifier: string): string {
  return identifier
    .split('')
    .map((char) => char.charCodeAt(0).toString(2).padStart(8, '0'))
    .join('')
    .split('')
    .map((bit) => (bit === '0' ? '\u200B' : '\u200C'))
    .join('');
}

const ENCODED_AI_MARKER = encodeIdentifier(AI_CONVERSATION_IDENTIFIER);

// A newline separates the reply body from the invisible marker. WITHOUT it, a
// reply that ENDS in a URL (e.g. "…board: https://app.blueticks.co/tasks/?…")
// gets the zero-width marker glued directly onto the URL, and WhatsApp's link
// detector includes those chars in the link — so the opened URL carries a
// `%E2%80%8B%E2%80%8C…` tail and 404s. The newline terminates the URL token
// (WhatsApp stops linkifying at whitespace); the marker still sits at the very
// end so the echo guards (endsWith / trailing-zero-width regex) keep matching.
const MARKER_SEP = '\n';

/** Append the hidden AI-conversation marker to an outgoing bridge reply. */
export function addAiConversationMarker(text: string): string {
  return `${text ?? ''}${MARKER_SEP}${ENCODED_AI_MARKER}`;
}

/** True when the text tail carries the btgpt bridge marker specifically. */
export function hasAiConversationMarker(text?: string): boolean {
  if (!text) return false;
  return text.endsWith(ENCODED_AI_MARKER);
}

/**
 * True when the text ends in ANY zero-width-encoded identifier tail (btgpt
 * bridge marker OR the chatbot's hidden bot identifier). Used by the linked-
 * chat ingest guards: any machine-tagged message must never re-trigger a turn,
 * whichever producer tagged it.
 */
export function hasAnyHiddenMarker(text?: string): boolean {
  if (!text) return false;
  const m = text.match(ZERO_WIDTH_TAIL_RE);
  // Require at least one full encoded byte to avoid false positives on stray
  // zero-width characters in ordinary text.
  return !!m && m[0].length >= 8 && m[0].length % 8 === 0;
}

/** Strip any zero-width marker tail (bridge or bot) — plus the newline
 *  separator the bridge adds before its marker — for display / history. */
export function stripAiConversationMarker(text?: string): string {
  if (!text) return '';
  return text.replace(MARKER_TAIL_STRIP_RE, '');
}

/** Exposed for tests / cross-checks. */
export const __AI_MARKER_INTERNALS = {
  identifier: AI_CONVERSATION_IDENTIFIER,
  encoded: ENCODED_AI_MARKER,
};
